import React, { useEffect, useState } from 'react';
import { HolidayResponse, Holiday } from '../../interfaces/holiday.interface';
import { HolidayService } from '../../services/holiday.service';
import { Box, Button, IconButton, Typography } from '@mui/material';
import ResponsiveTable from '../common/ResponsiveTable';
import { Column } from '../common/responsiveTable.types';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { format } from 'date-fns';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

const HolidayList: React.FC = () => {
    const [holidayData, setHolidayData] = useState<HolidayResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const toast = useToast();

    const canManage = usePermission(PERMISSIONS.HOLIDAY_MANAGE);
    const canUpdate = usePermission(PERMISSIONS.HOLIDAY_UPDATE);
    const canShowActions = canManage || canUpdate;

    useEffect(() => {
        fetchHolidays();
    }, []);

    const fetchHolidays = async () => {
        try {
            const data = await HolidayService.getHolidaysByInstitution();
            setHolidayData(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to fetch holidays');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this holiday?')) {
            try {
                await HolidayService.deleteHoliday(id);
                await fetchHolidays();
            } catch (err) {
                toast.showApiError(err, 'Failed to delete holiday');
            }
        }
    };

    const handleEdit = (holiday: Holiday) => {
        navigate(`/holidays/edit/${holiday.id}`, { state: { holiday } });
    };

    const columns: Column<Holiday>[] = [
        { key: 'name', header: 'Name', helpKey: 'holidays.col.name', priority: 1, render: h => h.name },
        { key: 'start', header: 'Start Date', helpKey: 'holidays.col.start', render: h => format(new Date(h.startDate), 'dd-MM-yyyy') },
        { key: 'end', header: 'End Date', helpKey: 'holidays.col.end', render: h => format(new Date(h.endDate), 'dd-MM-yyyy') },
        { key: 'desc', header: 'Description', helpKey: 'holidays.col.desc', render: h => h.description || '-' },
        ...(canShowActions ? [{
            key: 'actions', header: 'Actions', helpKey: 'holidays.col.actions', role: 'actions' as const,
            render: (h: Holiday) => (
                <>
                    {canUpdate && (
                        <IconButton data-help="holidays.action.edit" onClick={() => handleEdit(h)} color="primary" aria-label="edit"><EditIcon /></IconButton>
                    )}
                    {canManage && (
                        <IconButton data-help="holidays.action.delete" onClick={() => handleDelete(h.id)} color="error" aria-label="delete"><DeleteIcon /></IconButton>
                    )}
                </>
            ),
        }] : []),
    ];

    if (loading) return <Typography>Loading...</Typography>;
    if (!holidayData) return <Typography>No holidays found</Typography>;

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 } }} data-help-page="holidays.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Holidays</Typography>
                {canManage && (
                    <Button
                        data-help="holidays.add"
                        variant="contained"
                        color="primary"
                        onClick={() => navigate('/holidays/create')}
                    >
                        Add Holiday
                    </Button>
                )}
            </Box>
            <Box data-help-section="holidays.table">
            <ResponsiveTable<Holiday>
                pageKey="holidays.list"
                columns={columns}
                rows={holidayData.content}
                getRowKey={h => h.id}
                emptyMessage="No holidays found"
            />
            </Box>
        </Box>
    );
};

export default HolidayList;
